import React, { FC, useState, useRef, useEffect, useCallback } from 'react';
import { fabric } from 'fabric';
import useCanvasSizeChange from '../../../shared/UseCanvasSizeChange';
import { TABLE_STATUS } from '../../../shared/Helpers';
import useCanvasEvents from './Hooks/useCanvasEvents';
import { RootState } from '../../../rootReducer';
import { AppDispatch } from '../../../store';
import { connect } from 'react-redux';
import {
  setCanvasSize,
  imageSelected,
  imageLoaded,
  setActiveShape,
  setScaledOverlapping,
  annotationCreationCanceled,
  Overlap,
  imageUnloaded,
  setShouldZoomToFinding,
  setSaveFinding,
  generatePolygon,
  generateRectangle,
  toggleAllFindingsVisibilityChanged
} from './canvas.slice';
import {
  getFindingLocalizationDto,
  setLocalizationDto,
  undo,
  redo,
  preloadImages,
  setDirtyFlagOnFinding,
  setViewportTransform
} from '../Annotation/annotation.slice';
import { ShapePoint, AnnotationsForGrid, MLRequest, ImageRawData } from '../Annotation/annotation.types';
import { prepareShapesSelector } from './prepareShapesSelector';
import {
  setSelectedAnnotationForEdit,
  updateAnnotationShape,
  deleteAnnotation,
  setShowDeleteAnnotationDialog,
  setAnnotationIdsForDelete,
  setMultipleSelectedAnnotationsForEdit
} from '../Annotation/AnnotationForm/AnnotationForm.slice';
import { ValidationModalDto } from '../Annotation/AnnotationValidationModal/AnnotationValidationModal.slice';
import styles from './Canvas.module.scss';
import ConfirmationDialogAnt from '../../Shared/ConfirmationDialogAnt/ConfirmationDialogAnt';
import classNames from 'classnames';
import BusyContainer from './BusyContainer';
import ContextMenu from './ContextMenu';
import useUndoRedo from './Hooks/useUndoRedo';
import useFabricObjects from './Hooks/useFabricObjects';
import useCanvasHideFindings from './Hooks/useCanvasHideFindings';
import { prepareCanvasGridDataSelector } from './prepareCanvasGridDataSelector';

// fabric filters specific config
fabric.textureSize = 16384;
fabric.filterBackend = (fabric as any).initFilterBackend();

type CanvasProps = {
  rawImage: ImageRawData | null;
  hideTree: boolean;
  hideStichedView: boolean;
  contrast: number;
  brightness: number;
  saturation: number;
  stitchedViewWidth: number;
  treeWidth: number;
  setHoveredFinding: (hoverd: any | undefined) => void;
  setShowFindingOverlayOnLeft: (onLeft: boolean) => void;
  onAnnotationSelect: (sequencePath: string, imageName: string, id: string) => void;
};

type CanvasStoreProps = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;

const Canvas: FC<CanvasProps & CanvasStoreProps> = ({
  rawImage,
  polygonMode,
  rectangleMode,
  height,
  width,
  image,
  loaded,
  shapes,
  scale,
  gridData,
  overlappingDto,
  bottomOverplapping,
  createAnnotationCanceled,
  selectedName,
  fullScreenMode,
  selectedAnnotationId,
  annotationSetForUpdate,
  annotationForEdit,
  showDeleteAnnotationDialog,
  selectedAnnotationIdsForDelete,
  toggleAllUserFindingsVisibility,
  toggleMLFindingsVisibility,
  shouldZoomToFinding,
  hideTree,
  hideStichedView,
  toggleOverlappingVisibility,
  stitchedViewWidth,
  treeWidth,
  tableStatus,
  havePermissionToUse,
  presentAnnotations,
  saveFinding,
  annotationStandardDetails,
  contrast,
  brightness,
  saturation,
  multipleSelectedAnnotationsForEdit,
  viewportTransform,
  setHoveredFinding,
  setShowFindingOverlayOnLeft,
  onSetCanvasSize,
  onImageSelected,
  onImageLoaded,
  onGeneratePolygon,
  onGenerateRectangle,
  onSaveActiveShape,
  onAnnotationEdit,
  onScaledOverlapping,
  onLoadLocalization,
  resetCreateAnnotationCanceled,
  onUpdateAnnotationShape,
  onDeleteAnnotation,
  onShowDeleteDialog,
  onSetAnnotationIdsForDelete,
  onUndo,
  onRedo,
  onShouldZoomToFinding,
  onToggleAllFindingsVisibilityChanged,
  preloadClosestImages,
  onFindingShapeChanged,
  onFindingSave,
  onAnnotationSelect,
  onMultipleSelectedAnnotationsForEdit,
  saveViewportTransform
}) => {
  const [canvasEl, setCanvasEl] = useState<fabric.Canvas | null>(null);
  const canvasElRef = useRef<HTMLCanvasElement>(null);

  const [multiSelectObjects, setMultiSelectObjects] = useState<string[]>([]);
  const [normalCanvasWidth, setNormalCanvasWidth] = useState<number>(0);

  const sizeChangedHandler = useCallback(
    (canvasWidth, canvasHeight) => {
      onSetCanvasSize(canvasHeight, canvasWidth);
    },
    [onSetCanvasSize, fullScreenMode, hideTree, hideStichedView, stitchedViewWidth, treeWidth, tableStatus]
  );

  const preloadClosest = useCallback(() => {
    preloadClosestImages(selectedName);
  }, [preloadClosestImages, selectedName]);

  const loadLocalization = useCallback(
    (mlRequest, validationDto, isUpdate) => {
      onLoadLocalization(mlRequest, validationDto, isUpdate);
    },
    [onLoadLocalization]
  );

  const onSelectAnnotation = useCallback(
    (id: string) => {
      const annotation = gridData.find(x => x.id === id);

      if (selectedAnnotationId !== id) {
        onAnnotationSelect(annotation?.sequencePath ?? '', annotation?.imageName ?? '', annotation?.id ?? '');
      }
    },
    [gridData, onAnnotationSelect, selectedAnnotationId]
  );

  useEffect(() => {
    if (canvasEl?.viewportTransform) {
      const vt = canvasEl?.viewportTransform;

      let scale;
      if (fullScreenMode) {
        setNormalCanvasWidth(canvasEl?.width ? canvasEl?.width : 0);
        scale = canvasEl?.width ? window.innerWidth / canvasEl?.width : 1;
      } else {
        scale = window.innerWidth / normalCanvasWidth;
      }

      const newViewportTransform = fullScreenMode
        ? [vt[0], vt[1], vt[2], vt[3], vt[4] * scale, vt[5] * scale]
        : [vt[0], vt[1], vt[2], vt[3], vt[4] / scale, vt[5] / scale];

      saveViewportTransform(newViewportTransform);
    }
  }, [fullScreenMode]);

  useUndoRedo(onUndo, onRedo);
  useCanvasSizeChange(canvasEl, sizeChangedHandler, 'canvas-annotation-container');
  useCanvasEvents(
    canvasEl,
    polygonMode,
    rectangleMode,
    gridData,
    onGeneratePolygon,
    onGenerateRectangle,
    onSaveActiveShape,
    onAnnotationEdit,
    bottomOverplapping,
    scale, //TODO: check maybe there not good scale number,
    loadLocalization,
    onSelectAnnotation,
    fullScreenMode,
    shapes,
    onFindingShapeChanged,
    presentAnnotations,
    saveFinding,
    annotationStandardDetails,
    setHoveredFinding,
    setShowFindingOverlayOnLeft,
    onFindingSave,
    multiSelectObjects,
    setMultiSelectObjects
  );
  useFabricObjects(
    canvasEl,
    overlappingDto,
    scale,
    toggleOverlappingVisibility,
    shapes,
    toggleAllUserFindingsVisibility,
    toggleMLFindingsVisibility,
    height,
    image,
    loaded,
    createAnnotationCanceled,
    selectedAnnotationId,
    shouldZoomToFinding,
    havePermissionToUse,
    contrast,
    brightness,
    saturation,
    viewportTransform,
    onShouldZoomToFinding,
    onScaledOverlapping,
    onImageLoaded,
    resetCreateAnnotationCanceled,
    preloadClosest
  );

  useCanvasHideFindings(onToggleAllFindingsVisibilityChanged);

  useEffect(() => {
    const canvas = new fabric.Canvas(canvasElRef.current!);
    canvas.defaultCursor = 'grab';
    canvas.hoverCursor = 'pointer';
    setCanvasEl(canvas);
  }, []);

  useEffect(() => {
    if (!rawImage && !canvasEl) {
      return;
    }
    if (canvasEl) {
      canvasEl.clear();
    }
    if (rawImage && tableStatus !== TABLE_STATUS.EXPANDED) {
      onImageSelected(rawImage);
    }
  }, [canvasEl, tableStatus, height, onImageSelected, rawImage, width]);

  useEffect(() => {
    if (annotationSetForUpdate && annotationForEdit) {
      onUpdateAnnotationShape();
    }
  }, [annotationSetForUpdate, annotationForEdit, onUpdateAnnotationShape]);

  useEffect(() => {
    if (!multipleSelectedAnnotationsForEdit) {
      setMultiSelectObjects([]);
    }
  }, [multipleSelectedAnnotationsForEdit]);

  const showBusy = !!(tableStatus !== TABLE_STATUS.EXPANDED && !loaded && image);

  return (
    <>
      <div
        id='canvas-annotation-container'
        className={classNames(
          styles.canvasWrapper,
          { [styles.fullScreen]: fullScreenMode },
          { [styles.normalView]: !fullScreenMode },
          { [styles.minimized]: tableStatus === TABLE_STATUS.EXPANDED },
          { [styles.hiddenTable]: tableStatus === TABLE_STATUS.HIDE },
          { [styles.isFullScreenAndTableHidden]: fullScreenMode && tableStatus === TABLE_STATUS.HIDE }
        )}
      >
        <BusyContainer show={showBusy} />
        <canvas id='annotation' ref={canvasElRef} />
      </div>
      <ContextMenu
        {...{
          canvasEl,
          gridData,
          onSetAnnotationIdsForDelete,
          onShowDeleteDialog,
          onLoadLocalization,
          onAnnotationEdit,
          havePermissionToUse,
          saveFinding,
          onFindingSave,
          presentAnnotations,
          onMultipleSelectedAnnotationsForEdit,
          multiSelectObjects
        }}
      />
      <ConfirmationDialogAnt
        show={showDeleteAnnotationDialog}
        bodyMessage={'Do you want to delete this annotation(s)?'}
        onConfirm={() => {
          onShowDeleteDialog(false);
          onDeleteAnnotation(selectedAnnotationIdsForDelete!);
        }}
        onHide={() => onShowDeleteDialog(false)}
      />
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  height: state.canvas.height,
  width: state.canvas.width,
  loaded: state.canvas.loaded,
  imageLoading: state.canvas.loading,
  image: state.canvas.image,
  scale: state.canvas.imageScale,
  shapes: prepareShapesSelector(state),
  gridData: prepareCanvasGridDataSelector(state),
  bottomOverplapping: state.canvas.bottomOverplapping,
  createAnnotationCanceled: state.canvas.createAnnotationCanceled,
  toggleAllUserFindingsVisibility: state.canvas.toggleAllUserFindingsVisibility,
  toggleMLFindingsVisibility: state.canvas.toggleMLFindingsVisibility,
  toggleOverlappingVisibility: state.canvas.toggleOverlappingVisibility,
  shouldZoomToFinding: state.canvas.shouldZoomToFinding,
  saveFinding: state.canvas.saveFinding,
  polygonMode: state.canvas.polygonMode,
  rectangleMode: state.canvas.rectangleMode,
  annotationForEdit: state.annotationForm.selectedAnnotationForEdit,
  showDeleteAnnotationDialog: state.annotationForm.showDeleteAnnotationDialog,
  selectedAnnotationIdsForDelete: state.annotationForm.selectedAnnotationIdsForDelete,
  presentAnnotations: state.annotation.presentAnnotations,
  overlappingDto: state.annotation.image?.overlaps,
  selectedName: state.annotation.selectedName,
  fullScreenMode: state.annotation.fullScreenMode,
  selectedAnnotationId: state.annotation.selectedAnnotationId,
  annotationSetForUpdate: state.annotation.annotationSetForUpdate,
  tableStatus: state.annotation.tableStatus,
  annotationStandardDetails: state.annotation.annotationStandardDetails,
  havePermissionToUse: state.annotation.havePermissionToAnnotate,
  multipleSelectedAnnotationsForEdit: state.annotationForm.multipleSelectedAnnotationsForEdit,
  viewportTransform: state.annotation.viewportTransform
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  onSetCanvasSize: (height: number, width: number) => dispatch(setCanvasSize({ height, width })),
  onImageSelected: (rawImage: any) => dispatch(imageSelected(rawImage)),
  onImageLoaded: () => dispatch(imageLoaded()),
  preloadClosestImages: (name: string) => dispatch(preloadImages(name)),
  onImageUnloaded: () => dispatch(imageUnloaded()),
  onGeneratePolygon: (mode: boolean) => dispatch(generatePolygon(mode)),
  onGenerateRectangle: (mode: boolean) => dispatch(generateRectangle(mode)),
  onSaveActiveShape: (shape: ShapePoint[], imageScale: number, id: string) => dispatch(setActiveShape({ shape, imageScale, id })),
  onAnnotationEdit: (record: AnnotationsForGrid) => dispatch(setSelectedAnnotationForEdit(record)),
  onScaledOverlapping: (overlapping: Overlap[]) => dispatch(setScaledOverlapping(overlapping)),
  onLoadLocalization: (mlRequest: MLRequest, validationDto: ValidationModalDto, isUpdate: boolean = false) =>
    dispatch(getFindingLocalizationDto(mlRequest, validationDto, isUpdate)),
  resetCreateAnnotationCanceled: () => dispatch(annotationCreationCanceled(false)),
  onUpdateAnnotationShape: () => dispatch(updateAnnotationShape()),
  onDeleteAnnotation: (ids: string[]) => dispatch(deleteAnnotation(ids)),
  onShowDeleteDialog: (show: boolean) => dispatch(setShowDeleteAnnotationDialog(show)),
  onResetLocalization: () => dispatch(setLocalizationDto(null)),
  onSetAnnotationIdsForDelete: (ids: string[]) => dispatch(setAnnotationIdsForDelete(ids)),
  onUndo: () => dispatch(undo()),
  onRedo: () => dispatch(redo()),
  onShouldZoomToFinding: (shouldZoom: boolean) => dispatch(setShouldZoomToFinding(shouldZoom)),
  onToggleAllFindingsVisibilityChanged: () => dispatch(toggleAllFindingsVisibilityChanged()),
  onFindingShapeChanged: (findingId: string) => dispatch(setDirtyFlagOnFinding(findingId)),
  onFindingSave: (saveFinding: boolean) => dispatch(setSaveFinding(saveFinding)),
  onMultipleSelectedAnnotationsForEdit: (annotations: AnnotationsForGrid[] | null) => dispatch(setMultipleSelectedAnnotationsForEdit(annotations)),
  saveViewportTransform: (viewportTransform: number[] | undefined) => dispatch(setViewportTransform(viewportTransform))
});

export default connect(mapStateToProps, mapDispatchToProps)(Canvas);
